import React from 'react';
import { ThemeProvider } from "@/providers/ThemeProvider";
import { QueryProvider } from "@/providers/QueryProvider";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from 'react-oidc-context';
import { useProduct, ProductProvider, useProductInstance } from '@workspace/utils-react';
import { getOidcConfig } from '@/config/oidcConfig';
import App from "@/App";
import NativeTabLoginCallback from '@/pages/NativeTabLoginCallback';
import StandaloneLoginCallback from '@/pages/StandaloneLoginCallback';
import { AppModeProvider, useAppMode, AppMode } from '@/contexts/AppModeContext';
import { IdentityOverrideContext } from '@/hooks/useIdentity';
import { SubscriptionProvider } from '@/contexts/SubscriptionContext';
import { NativeTabTokenProvider, StandaloneTokenProvider, DevAuthTokenProvider } from '@/contexts/TokenProviderContext';
import { FeatureToggleProvider } from '@/contexts/FeatureToggleContext';

/**
 * Main App Container with Access Identity Integration
 * 
 * This component supports three authentication modes:
 * 
 * 1. NATIVE TAB MODE (inside Access Workspace iframe):
 *    - Authority URL from Access Workspace manifest settings
 *    - Silent authentication (no login UI)
 *    - ProductProvider enabled for Access Workspace APIs
 *    - Subscription ID from ProductProvider
 * 
 * 2. STANDALONE MODE (direct browser access):
 *    - Authority URL from VITE_IDENTITY_URL environment variable
 *    - Interactive authentication (redirects to login page)
 *    - Full login UI with Access Identity
 *    - Subscription ID from API + user selection
 * 
 * 3. E2B MODE (e2b.app environment):
 *    - Mock authentication (no login)
 *    - Instant access for development
 *    - Hardcoded subscription ID
 * 
 * Native Tab and Standalone modes use the same OIDC client configuration and token management.
 */

/**
 * Native Tab App Wrapper
 * 
 * Gets instanceId from workspace SDK and passes to App
 */
function NativeTabApp() {
  const instance = useProductInstance();

  return (
    <NativeTabTokenProvider>
      <SubscriptionProvider instanceId={instance?.instanceId}>
        <FeatureToggleProvider>
        <QueryProvider>
          <TooltipProvider>
            <ThemeProvider
              attribute="class"
              defaultTheme="system"
              enableSystem
              disableTransitionOnChange
            >
              <Toaster
                richColors
                duration={3000}
                closeButton
                position="bottom-right"
                style={{ maxWidth: '300px' }}
              />
              <App instanceId={instance?.instanceId} />
            </ThemeProvider>
          </TooltipProvider>
        </QueryProvider>
        </FeatureToggleProvider>
      </SubscriptionProvider>
    </NativeTabTokenProvider>
  );
}

function NativeTabAppContainer() {
  const product = useProduct();
  const oidcConfig = getOidcConfig();

  const authority = product?.settings?.identityUrl;
  
  if(authority){
      return (
        <ProductProvider iframePidget={product}>
          <AuthProvider {...oidcConfig} authority={authority}>
            <BrowserRouter>
              <Routes>
                {/* Main app routes with authentication */}
                <Route
                  path="*"
                  element={<NativeTabApp />}
                />
              </Routes>
            </BrowserRouter>
          </AuthProvider>
        </ProductProvider>
      );
  }

  // For silent callback iframe
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login/callback" element={<NativeTabLoginCallback />} />
      </Routes>
    </BrowserRouter>
  );
}

/**
 * Standalone App Wrapper
 * 
 * Wraps App with SubscriptionProvider for subscription management
 */
function StandaloneApp() {
  return (
    <StandaloneTokenProvider>
      <SubscriptionProvider>
        <FeatureToggleProvider>
        <QueryProvider>
          <TooltipProvider>
            <ThemeProvider
              attribute="class"
              defaultTheme="system"
              enableSystem
              disableTransitionOnChange
            >
              <Toaster
                richColors
                duration={3000}
                closeButton
                position="bottom-right"
                style={{ maxWidth: '300px' }}
              />
              <App />
            </ThemeProvider>
          </TooltipProvider>
        </QueryProvider>
        </FeatureToggleProvider>
      </SubscriptionProvider>
    </StandaloneTokenProvider>
  );
}

/**
 * Standalone App Container
 * 
 * For direct browser access (localhost).
 * Uses REAL OIDC authentication with redirect flow.
 * User must login via Access Identity.
 * Subscription ID fetched from API after authentication.
 */
function StandaloneAppContainer() {

  const oidcConfig = getOidcConfig();
  const authority = import.meta.env.VITE_IDENTITY_URL;

  return (
    <AuthProvider {...oidcConfig} authority={authority}>
      <BrowserRouter>
        <Routes>
          {/* Login callback route */}
          <Route path="/login/callback" element={<StandaloneLoginCallback />} />
          
          {/* Main app routes with authentication */}
          <Route path="*" element={<StandaloneApp />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

/**
 * E2B App Container
 * 
 * For e2b.app environment (AI agent development).
 * Uses MOCK authentication - NO AuthProvider needed!
 * useIdentity hook returns mock user data automatically.
 * User is instantly "authenticated" as admin@example.com.
 * No subscription ID header is sent in E2B mode.
 */
function E2BAppContainer() {
  
  // NO AuthProvider - useIdentity returns mock data directly
  // NO instanceId - E2B mode doesn't send subscription header
  return (
    <BrowserRouter>
      <Routes>
        {/* Main app routes - instant access, no login! */}
        <Route 
          path="*" 
          element={
            <FeatureToggleProvider>
            <QueryProvider>
              <TooltipProvider>
                <ThemeProvider 
                  attribute="class" 
                  defaultTheme="system" 
                  enableSystem 
                  disableTransitionOnChange
                >
                  <Toaster 
                    richColors 
                    duration={3000} 
                    closeButton 
                    position="bottom-right"
                    style={{ maxWidth: '300px' }}
                  />
                  <App />
                </ThemeProvider>
              </TooltipProvider>
            </QueryProvider>
            </FeatureToggleProvider>
          } 
        />
      </Routes>
    </BrowserRouter>
  );
}

/**
 * Dev Auth App Container
 *
 * For local development with backend DevAuth bypass.
 * NO AuthProvider — backend injects claims via DevAuthBypassMiddleware.
 * Skips OIDC entirely, avoiding identity server CORS issues.
 */
function DevAuthAppContainer() {
  const devIdentity = React.useMemo(() => ({
    signin: async () => null,
    signinRedirect: async () => {},
    signout: async () => {},
    loading: false,
    error: null,
    isAuthenticated: true,
    user: null,
    getAccessToken: async () => 'dev-auth-token',
  }), []);

  return (
    <IdentityOverrideContext.Provider value={devIdentity}>
      <BrowserRouter>
        <Routes>
          <Route
            path="*"
            element={
              <DevAuthTokenProvider>
                <SubscriptionProvider>
                  <FeatureToggleProvider>
                    <QueryProvider>
                      <TooltipProvider>
                        <ThemeProvider
                          attribute="class"
                          defaultTheme="system"
                          enableSystem
                          disableTransitionOnChange
                        >
                          <Toaster
                            richColors
                            duration={3000}
                            closeButton
                            position="bottom-right"
                            style={{ maxWidth: '300px' }}
                          />
                          <App />
                        </ThemeProvider>
                      </TooltipProvider>
                    </QueryProvider>
                  </FeatureToggleProvider>
                </SubscriptionProvider>
              </DevAuthTokenProvider>
            }
          />
        </Routes>
      </BrowserRouter>
    </IdentityOverrideContext.Provider>
  );
}

/**
 * App Container Router
 *
 * Uses AppMode context to route to the correct container
 */
function AppContainerRouter() {
  const { mode } = useAppMode();

  if (mode === AppMode.DEV_AUTH) {
    return <DevAuthAppContainer />;
  }

  if (mode === AppMode.NATIVE_TAB) {
    return <NativeTabAppContainer />;
  }

  if (mode === AppMode.STANDALONE) {
    return <StandaloneAppContainer />;
  }

  // E2B mode
  return <E2BAppContainer />;
}

/**
 * Root App Container
 * 
 * Wraps everything in AppModeProvider for global mode detection
 */
export default function AppContainer() {
  return (
    <AppModeProvider>
      <AppContainerRouter />
    </AppModeProvider>
  );
}
